/**
 * Supported languages and the URL shape that carries them.
 *
 * English lives at the root (`/faq`); every other language is prefixed (`/fr/faq`). That
 * keeps the canonical English URLs stable, gives each translation its own indexable
 * address, and makes the active language readable from the path alone with no cookie or
 * server state involved.
 */

export const LOCALES = ['en', 'fr', 'es', 'pt'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** Names shown in the language menu, each written in its own language. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  pt: 'Português',
};

/** Short label for the collapsed menu button. */
export const LOCALE_SHORT: Record<Locale, string> = {
  en: 'EN',
  fr: 'FR',
  es: 'ES',
  pt: 'PT',
};

/** `lang` and `hreflang` attribute values. */
export const LOCALE_TAGS: Record<Locale, string> = {
  en: 'en',
  fr: 'fr',
  es: 'es',
  pt: 'pt',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Split a pathname into its locale and the underlying route.
 * `/fr/faq` becomes `{ locale: 'fr', path: '/faq' }`; `/faq` stays English.
 */
export function splitLocalePath(pathname: string): { locale: Locale; path: string } {
  const match = /^\/([a-z]{2})(\/.*)?$/.exec(pathname);
  if (match && isLocale(match[1])) {
    return { locale: match[1], path: match[2] && match[2] !== '/' ? match[2] : '/' };
  }
  return { locale: DEFAULT_LOCALE, path: pathname };
}

/**
 * Build the URL for a route in a given language.
 *
 * Always with a trailing slash, because that is what the static-asset server serves:
 * `/faq` answers 307 and sends the browser to `/faq/`. Emitting the redirect target
 * directly saves a round trip on every internal link, and keeps the canonical and
 * hreflang URLs pointing at addresses that answer 200 rather than at redirects, which
 * search engines are explicit about disliking.
 */
export function localePath(locale: Locale, path: string): string {
  const clean = path === '/' || path.endsWith('/') ? path : `${path}/`;
  const prefix = locale === DEFAULT_LOCALE ? '' : `/${locale}`;
  return `${prefix}${clean}`;
}

/**
 * Pick the best supported language from an `Accept-Language` header or from
 * `navigator.languages`.
 *
 * Quality values are honoured, and a regional tag matches its base language, so
 * `pt-BR` selects Portuguese and `fr-CA` selects French. Returns null when nothing
 * matches, which the caller treats as "leave them on English".
 */
export function matchLocale(accepted: readonly string[] | string | null | undefined): Locale | null {
  if (!accepted) return null;

  const entries = (typeof accepted === 'string' ? accepted.split(',') : [...accepted])
    .map((raw, index) => {
      const [tag = '', ...params] = raw.trim().split(';');
      const q = params.find((p) => p.trim().startsWith('q='));
      const quality = q ? Number.parseFloat(q.split('=')[1] ?? '1') : 1;
      return {
        base: tag.trim().toLowerCase().split('-')[0] ?? '',
        quality: Number.isFinite(quality) ? quality : 1,
        index,
      };
    })
    .filter((entry) => entry.base !== '' && entry.quality > 0)
    // Stable sort: quality first, then the order the browser listed them.
    .sort((a, b) => b.quality - a.quality || a.index - b.index);

  for (const entry of entries) {
    if (isLocale(entry.base)) return entry.base;
  }
  return null;
}
